import React, { useRef } from 'react'
import { useAlerts } from '@theneatcompany/nui'

import { useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import Form from '@/components/forms/Form'
import { Column, Box } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import GoogleLoginButton from '@/components/Pages/SignUp/GoogleLoginButton'
import CheckoutSideEmail from '@/components/blocks/CheckoutSideEmail'
import CheckoutSideEmailNeatHome from '@/components/blocks/CheckoutSideEmailNeatHome'
import { Link } from '@/components/common'
import { WEB_APP_URL } from '@/config'
import useResponsiveDimensions from '@/hooks/useResponsiveDimensions'
import { NEAT_HOME_TRIAL_SKU } from '@/constants'

import FormBox from './FormBox'
import styles from './SignUp.module.scss'
import SignUpLayout from './SignUpLayout'

const DISALLOWED_EMAIL_DOMAINS = ['@emailboxi.live']

const getMessageId = id => `pages.signUp.form.authenticate.${id}`

const UserLookup = () => {
  const {
    saveStepAuthenticate,
    plan,
  } = useSignUp()
  const alerts = useAlerts()
  const schema = Yup.object({
    email: Yup.string().trim().lowercase().email('Invalid email format')
      .required('An email is required to continue.'),
  })

  const formRef = useRef(null)
  const formDimensions = useResponsiveDimensions(formRef)

  const saveData = async (model) => {
    if (!model.email || DISALLOWED_EMAIL_DOMAINS.some(d => model.email.includes(d))) {
      alerts.error('Please provide an email or login with Google')
      return
    }

    await saveStepAuthenticate(model)
  }

  const handleGoogleLogin = async (response = {}) => {
    await saveStepAuthenticate({}, response?.credential)
  }

  return (
    <SignUpLayout sideContent={plan.sku === 'neathomeyr' || plan.sku === 'neathomevipyr' || plan.sku === NEAT_HOME_TRIAL_SKU ? <CheckoutSideEmailNeatHome /> : <CheckoutSideEmail />}>
      <div className={styles.aboveFormSection}>
        {(plan.sku === 'neatyr' || plan.sku === 'neatvipyr' || plan.sku === 'neataiyr') && (
          <h2 className="text-center">
            Buy a plan today.<br />
            <span>30-day money back</span><br />
            guarantee!
          </h2>
        )}
        {plan.sku === 'neatyrt' && (
          <h2 className="text-center">
            Start your <span>FREE</span> 14-day,<br />
            risk-free trial & access<br />
            Neat's base plan!
          </h2>
        )}
        {(plan.sku === 'neathomeyr' || plan.sku === 'neathomevipyr') && (
          <h2 className="text-center">
            Buy NeatHome today.<br />
            <span>30-day money back</span><br />
            guarantee!
          </h2>
        )}
        {plan.sku === NEAT_HOME_TRIAL_SKU && (
          <h2 className="text-center">
            Start your <span>FREE</span> 14-day,<br />
            risk-free trial & access<br />
            NeatHome today!
          </h2>
        )}
      </div>
      <FormBox shouldCalculateMargin={false} withPricingBar={false}>
        <p className="text-center">Enter your email to get started.</p>
        <Form ref={formRef} initialValues={{ email: '' }} onSubmit={saveData} schema={schema}>
          <Column>
            <InputField
              data-testid="signup-email-input-field"
              helperProps={{
                'data-testid': 'email-validation-error',
              }}
              labelProps={{ hidden: true }}
              name="email"
              placeholder="Enter Your Email"
              size="large"
            />
          </Column>
          <Form.Action
            className={styles.submit}
            data-testid="signup-get-started-cta"
            messageId={getMessageId('nextButton')}
            size="large"
            type="submit"
          />
          <div className={styles.orGoogle} />
          <Column alignCenter>
            <GoogleLoginButton onSuccess={handleGoogleLogin} width={Math.ceil(formDimensions.width)} />
          </Column>
        </Form>
        <p className="text-center">Already an active subscriber? <Link className={styles.noUnderline} to={WEB_APP_URL}>Log In</Link></p>
      </FormBox>
      <Box center>
        <Link className={styles.securityLink} target="_blank" to="/policies/privacy/">Security &amp; Privacy</Link>
      </Box>

    </SignUpLayout>

  )
}

export default UserLookup
